import React, { useRef, useEffect, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, useGLTF } from '@react-three/drei';
import { Button } from 'antd';
import * as THREE from 'three';
import './CarModel.css';

const Car = ({ temperatureData }) => {
  const group = useRef();
  const { scene } = useGLTF('/car.gltf'); // Ensure the path is correct

  // Function to map temperature to color
  const getColorFromTemperature = (temperature) => {
    let color = new THREE.Color();

    if (temperature <= 20) {
      // Green for good
      color.setRGB(0, 1, 0);
    } else if (temperature <= 40) {
      // Yellow for warmer
      color.setRGB(1, 1, 0);
    } else if (temperature <= 60) {
      // Orange for much warmer
      color.setRGB(1, 0.5, 0);
    } else {
      // Red for HOT
      color.setRGB(1, 0, 0);
    }

    // Adjust the color slightly based on the exact temperature value
    if (temperature > 20 && temperature <= 40) {
      const factor = (temperature - 20) / 20;
      color.lerp(new THREE.Color(1, 1, 0), factor);
    } else if (temperature > 40 && temperature <= 60) {
      const factor = (temperature - 40) / 20;
      color.lerp(new THREE.Color(1, 0.5, 0), factor);
    } else if (temperature > 60) {
      const factor = (temperature - 60) / 20;
      color.lerp(new THREE.Color(1, 0, 0), factor);
    }

    return color;
  };

  useEffect(() => {
    if (scene && temperatureData) {
      scene.traverse((child) => {
        if (child.isMesh) {
          const geometry = child.geometry;
          const positionAttribute = geometry.attributes.position;
          const colorAttribute = new THREE.BufferAttribute(new Float32Array(positionAttribute.count * 3), 3);

          // Assign temperature values to vertices based on their positions
          for (let i = 0; i < positionAttribute.count; i++) {
            const vertex = new THREE.Vector3().fromBufferAttribute(positionAttribute, i);

            // Define explicit regions and assign colors for visualization
            let temperature;
            if (vertex.x < 1000 && vertex.y < 1000) {
              temperature = temperatureData.bottomLeft;
            } else if (vertex.x > 1000 && vertex.y < 1000) {
              temperature = temperatureData.bottomRight;
            } else if (vertex.x > 1000 && vertex.y > 1000) {
              if (vertex.y > 1600 && vertex.y < 2900) {
                temperature = temperatureData.centerRightForward;
              } else if (vertex.y < 1600) {
                temperature = temperatureData.centerRightBackward;
              } else if (vertex.y >= 2900) {
                temperature = temperatureData.centerRight;
              }
            } else {
              temperature = -40; // Default temperature for regions not explicitly defined
            }

            // Map temperature to a color gradient
            const color = getColorFromTemperature(temperature);

            colorAttribute.setXYZ(i, color.r, color.g, color.b);
          }

          geometry.setAttribute('color', colorAttribute);

          child.material = new THREE.MeshBasicMaterial({ vertexColors: true, wireframe: true });
        }
      });
    }
  }, [scene, temperatureData]);

  return (
    <group ref={group} dispose={null} position={[-7, -15, -5]}>
      <primitive object={scene} scale={0.01} /> {/* Adjust the scale to fit the model */}
    </group>
  );
};

const CarModel = ({ temperatureData }) => {
  const [key, setKey] = useState(0);

  const handleReset = () => {
    setKey(prevKey => prevKey + 1);
  };

  return (
    <div className="car-model-container">
      <Button onClick={handleReset} style={{ marginBottom: '10px' }}>Reset Car Model</Button>
      <Canvas key={key} camera={{ position: [0, 20, 50], fov: 60 }}>
        <ambientLight />
        <Car temperatureData={temperatureData} />
        <OrbitControls enableZoom={false} />
      </Canvas>
    </div>
  );
};

export default CarModel;
